/**
 * Cable profiles -- group 01 (the large American specialist houses).
 *
 * AudioQuest, Cardas, Nordost, Transparent, MIT, Wireworld, Synergistic
 * Research and Analysis Plus. Between them these eight brands cover almost the
 * whole range of construction ideas in high-end cable: counter-spiralled
 * bundles, golden-section litz, flat spaced ribbons, network boxes, hollow
 * oval conductors and air-tube dielectrics.
 *
 * None of these companies publishes resistance, inductance and capacitance the
 * way Belden or Canare do, so with a single exception nothing in this file
 * passes electrical figures in: the kit derives them from gauge, material and
 * geometry, and the entries say so. Where a gauge is inferred from conductor
 * count rather than quoted, the provenance note states that too.
 *
 * Two of these makers -- Transparent and MIT -- build a passive filter network
 * into a moulded box in series with the cable, and neither publishes what is
 * inside it. The engine models the conductors only. That is stated on every
 * affected entry rather than glossed over.
 *
 * And the standing rule for the directory: construction and electrical
 * consequence only. No cable in here is described as warmer, blacker, airier or
 * faster, because the engine cannot compute any of those and this database does
 * not assert them.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_01: CableProfile[] = [
  // =========================================================================
  // AudioQuest -- Irvine, California
  // =========================================================================
  makeCable({
    mfr: 'AudioQuest',
    model: 'Type 4',
    cableType: 'speaker',
    price: usd(260, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m factory-terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    dielectric: 'Foamed polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['solid-conductors', 'entry-line', 'long-grain-copper'],
    notes:
      'Four solid Long-Grain Copper conductors, two per polarity, run flat and parallel under one jacket in foamed polyethylene. Solid conductors mean the cross-section is one piece of copper rather than a bundle of strand surfaces, which is a verifiable manufacturing fact and has no effect on the arithmetic: at 14 AWG loop resistance is about 0.017 ohm/m, so a 3 m run puts roughly 0.05 ohm in series with the loudspeaker. That is under 0.06 dB into 8 ohms and about 0.14 dB where the impedance falls to 3.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Rocket 11',
    cableType: 'speaker',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m factory-terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Foamed polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor material, dielectric and the counter-spiralled architecture are as AudioQuest describes them. The gauge is the approximate total copper cross-section inferred from conductor count and size, not a published AWG figure. Electrical values are derived by the kit from that gauge and geometry.',
    accent: '#1f6feb',
    tags: ['counter-spiral', 'mid-line', 'hollow-core'],
    notes:
      'Six solid Long-Grain and Perfect-Surface Copper conductors counter-spiralled around a hollow centre, so adjacent runs carry opposing current over part of their length and much of the external field cancels. That is the mechanism behind the low loop inductance of a braided or spiralled cable, and the price of it is capacitance an order of magnitude above a simple parallel pair -- irrelevant into a loudspeaker, ruinous if anyone used the same geometry as an interconnect. Total copper is around 12 AWG, so loop resistance sits near 0.010 ohm/m.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Rocket 33',
    cableType: 'speaker',
    price: usd(1150, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m factory-terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Foamed polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Architecture and conductor material are as published. Gauge is the inferred total copper cross-section rather than a quoted AWG figure, and the electrical values follow from it by derivation.',
    accent: '#1f6feb',
    tags: ['counter-spiral', 'bi-wire-capable', 'perfect-surface-copper'],
    notes:
      'The same counter-spiralled architecture as the smaller Rockets with more and larger Perfect-Surface Copper conductors, and separate conductor groups per polarity so the cable can be run single-wired, bi-wired or shotgun from one length. At roughly 11 AWG the loop resistance falls to about 0.008 ohm/m: over 3 m that is 0.025 ohm against the 0.079 ohm a 16 AWG cable would drop over the same distance. Whether 0.05 ohm matters depends entirely on how far the loudspeaker impedance dips, which the engine works out from the actual load curve.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Robin Hood ZERO',
    cableType: 'speaker',
    price: usd(4000, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m factory-terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 12,
    dielectric: 'Air-filled polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor material, dielectric and the 72 V bias pack are as AudioQuest publishes them. Gauge is inferred from conductor count and size. The bias pack carries no signal and nothing is modelled for it.',
    accent: '#1f6feb',
    tags: ['spaced-solid-conductors', 'dielectric-bias', 'flagship-line'],
    notes:
      'Solid Perfect-Surface Copper Plus conductors in air-filled polyethylene, spaced apart inside the jacket rather than bundled, with a 72 V battery pack polarising the dielectric. Spaced solid conductors are the low-capacitance, higher-inductance end of the trade-off: around 2.3 uH over a 3 m run, whose reactance at 20 kHz is roughly 0.28 ohm -- small, but the one number here worth checking against a speaker that dips to 3 ohms in the top octave. The bias pack is a real component with a real battery in it and it is outside the signal path entirely.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Evergreen',
    cableType: 'interconnect',
    price: usd(45, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    dielectric: 'Foamed polyethylene',
    shielding: 'Foil with drain wire',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['solid-conductors', 'entry-line', 'moulded-plugs'],
    notes:
      'Solid Long-Grain Copper conductors in foamed polyethylene with a foil shield and drain, in moulded RCA plugs. Spaced solid conductors keep capacitance around 45 pF/m, so a 1 m pair loads the source with about 45 pF; against a 100 ohm solid-state output that puts the low-pass corner above 35 MHz. The engine computes the corner from the real numbers and reports the in-band result, which for this cable is 0.00 dB.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Mackenzie',
    cableType: 'interconnect',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    dielectric: 'Foamed polyethylene',
    shielding: 'Carbon-loaded noise-dissipation layer under a braid, grounded at the source end only',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['balanced', 'source-grounded-shield', 'mid-line'],
    notes:
      'Solid Perfect-Surface Copper Plus conductors, one per polarity, with a carbon-loaded dissipation layer under the braid and the shield tied at the source end only so the drain cannot become a second signal return. Twisted-pair geometry puts capacitance near 70 pF/m, so even into a 600 ohm tube output stage a 1 m run corners above 3 MHz. The construction choice that actually matters here is the single-ended shield grounding, which is a wiring fact rather than a sonic claim.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Fire',
    cableType: 'interconnect',
    price: usd(3900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 21,
    dielectric: 'Air-filled polyethylene',
    shielding: 'Carbon-based dissipation layers with a 72 V dielectric bias pack',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['solid-silver', 'dielectric-bias', 'flagship-line'],
    notes:
      'Solid Perfect-Surface Silver conductors in an air-filled dielectric with a 72 V bias pack on the insulation. Silver lowers bulk resistivity about eight per cent against copper, which on a 1 m interconnect passing microamps into a 10 kohm input is arithmetically beside the point -- the series resistance of the cable is already thousands of times smaller than the load. What is being paid for is metallurgy, dielectric volume and termination, all of which are real manufacturing and none of which changes the three numbers the engine reads.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Cinnamon USB',
    cableType: 'usb',
    price: usd(60, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    dielectric: 'Foamed polyethylene',
    shielding: 'Metal-layer shield',
    termination: ['USB A to B', 'USB A to C', 'USB C to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['usb-2.0', 'silver-plated', 'compliant'],
    notes:
      'Long-Grain Copper conductors with 1.25 per cent silver plating, foamed-polyethylene insulation, separate conductors for the data pair and the bus power rail, and a metal-layer shield. It is a compliant 90 ohm USB 2.0 assembly and the plating is a genuine construction difference from a commodity cable. The engine applies no signal processing to USB, so this entry contributes its build description and its price and nothing else.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Diamond USB',
    cableType: 'usb',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    dielectric: 'Foamed polyethylene',
    shielding: 'Metal-layer shield with a 72 V dielectric bias pack',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['usb-2.0', 'solid-silver', 'dielectric-bias'],
    notes:
      'Solid Perfect-Surface Silver conductors on the data pair, foamed-PE dielectric, a 72 V bias pack on the shield and hard-silver-plated contacts. As manufacturing it is far beyond what the USB specification asks for, and the parts are exactly what AudioQuest says they are. As a link it either delivers USB 2.0 packets intact or it fails, and in an asynchronous DAC the clock is made at the DAC rather than recovered from the cable, so the engine applies no processing to this type.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Vodka Ethernet',
    cableType: 'ethernet',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Foamed polyethylene',
    shielding: 'Full metal-layer shield',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['solid-conductors', 'silver-plated', 'shielded'],
    notes:
      'Solid Long-Grain Copper conductors with ten per cent silver plating, foamed-polyethylene insulation, a full metal-layer shield and hard-silver-plated RJ45 contacts. Solid conductors in a patch lead are unusual and have a practical consequence: the cable is stiff and does not tolerate repeated tight flexing the way stranded patch cable does. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds ahead of playback, so the engine applies no signal processing here.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'NRG-X3',
    cableType: 'power',
    price: usd(140, 'CURRENT_RETAIL', 2024, 'Price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6feb',
    tags: ['15a', 'moulded-terminations', 'entry-line'],
    notes:
      'Three Long-Grain Copper conductors in a jacketed cord with moulded IEC and wall plugs, sized for a 15 A circuit. Gauge and rating are the specifications that mean anything on a power cord: 14 AWG carries 15 A over normal lengths without meaningful heating. The engine applies no signal processing to power cords, so selecting this changes the chain description and the price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'AudioQuest',
    model: 'Thunder',
    cableType: 'power',
    price: usd(1000, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 20,
    shielding: 'Carbon-based noise-dissipation layers over the conductors',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Series, conductor material and termination options are as published; the per-conductor gauge is inferred from the stated current rating and the physical size of the cord. Power cords receive no DSP in this engine.',
    accent: '#1f6feb',
    tags: ['20a', 'high-current', 'storm-series'],
    notes:
      'Storm Series cord built from solid Perfect-Surface Copper conductors with carbon-based dissipation layers, offered with 15 A and 20 A IEC connectors. At roughly 12 AWG per conductor it is properly rated for a power amplifier that pulls large transient current from the wall, which is a thermal and safety specification rather than an audio one. This engine applies identity to power cords: the cord, its gauge and its price are real, and its DSP contribution is exactly zero.',
  }),

  // =========================================================================
  // Cardas Audio -- Bandon, Oregon
  // =========================================================================
  makeCable({
    mfr: 'Cardas Audio',
    model: 'Iridium Speaker',
    cableType: 'speaker',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 14,
    dielectric: 'PFA',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['golden-section-litz', 'entry-line', 'enamelled-strands'],
    notes:
      'The entry point of the Cardas range: individually enamelled copper strands assembled in the golden-section size progression the company builds everything around, jacketed as a two-conductor cable. Litz construction means the bundle behaves as many small isolated conductors rather than one large one, which pushes the frequency at which skin effect begins to matter upward. At 14 AWG the loop resistance is about 0.017 ohm/m, and that resistance, not the strand arrangement, is what the loudspeaker actually sees.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Parsec Speaker',
    cableType: 'speaker',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    dielectric: 'Crosslinked polyethylene with air space',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['golden-section-litz', 'mid-line'],
    notes:
      'A heavier version of the same golden-section litz bundle in a crosslinked polyethylene and air dielectric. The step from 14 to 12 AWG is the part that shows on a meter: loop resistance drops from roughly 0.017 to 0.010 ohm/m, so a 2.5 m run drops about 0.026 ohm rather than 0.042. Into a 4 ohm minimum that is the difference between about 0.09 dB and 0.06 dB of level loss, which is the whole of the measurable difference between these two cables.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Cygnus Speaker',
    cableType: 'speaker',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 11,
    dielectric: 'Crosslinked polyethylene with air space',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor architecture and dielectric are as Cardas describes them. Gauge is the inferred total copper cross-section of the array rather than a published AWG figure, and Cardas does not publish R, L or C, so all three are derived here.',
    accent: '#b07d2b',
    tags: ['clear-series', 'golden-section-litz', 'large-array'],
    notes:
      'Clear-series geometry: matched conductor arrays in crosslinked PE and air, with strand sizes following the golden ratio so that no two share a length between crossings. Physically it is a large, heavy litz bundle of around 11 AWG, giving about 0.008 ohm/m of loop resistance. Cardas publishes no electrical figures for it, so the numbers attached to this entry are derived from the gauge and geometry and are labelled as such rather than presented as measurements.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Beyond Speaker',
    cableType: 'speaker',
    price: usd(9000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 10,
    dielectric: 'Crosslinked polyethylene with air space',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published by Cardas; gauge inferred from the size of the conductor array. No electrical figures are published for this model, so all three are derived.',
    accent: '#b07d2b',
    tags: ['clear-series', 'flagship', 'rhodium-plated-spades'],
    notes:
      'The flagship litz array, with more and larger conductor groups again and rhodium-plated Cardas spades. At around 10 AWG the loop resistance is near 0.0066 ohm/m, so a 2.5 m run adds about 0.016 ohm -- the region where further increases in gauge stop changing anything measurable into any normal loudspeaker. Above that point what is being bought is mechanical execution and termination quality, which are real and which the engine does not model.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Iridium Interconnect',
    cableType: 'interconnect',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 23,
    dielectric: 'PFA',
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['golden-section-litz', 'entry-line', 'grmo-plugs'],
    notes:
      'Golden-section litz conductors in a shielded pair, terminated in the Cardas GRMO RCA or an XLR. Litz in this geometry sits near 120 pF/m, so a 1 m pair presents roughly 120 pF to the source: against a 100 ohm output that corners above 13 MHz, and against a 2 kohm tube output around 660 kHz. Both are well outside the audio band, and that is what the engine will report rather than a difference it cannot find.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Parsec Interconnect',
    cableType: 'interconnect',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 22,
    dielectric: 'PFA with air space',
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['golden-section-litz', 'mid-line', 'balanced'],
    notes:
      'The same golden-section litz idea with a larger conductor count and more air in the dielectric. In balanced form the two legs are matched bundles with the shield carried separately, so the shield is not part of the return path -- the one wiring decision in an interconnect with a genuine, checkable consequence for common-mode rejection. Capacitance stays around 120 pF/m and the derived in-band loss is nil to two decimal places.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Reflection Interconnect',
    cableType: 'interconnect',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 22,
    dielectric: 'Crosslinked polyethylene with air space',
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described by Cardas. No electrical figures are published for the Clear series, so resistance, inductance and capacitance are derived from gauge, material and geometry.',
    accent: '#b07d2b',
    tags: ['clear-series', 'balanced', 'air-dielectric'],
    notes:
      'Clear-series conductor arrays in a mostly-air dielectric with a braided shield. The dielectric volume is the real construction difference against the cheaper Cardas cables: more air means a lower dielectric constant and therefore lower capacitance per metre for the same spacing. On a 1 m interconnect that moves an already-inaudible corner frequency further out of the band, which is worth stating plainly rather than dressing up.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Beyond Interconnect',
    cableType: 'interconnect',
    price: usd(4300, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 21,
    dielectric: 'Crosslinked polyethylene with air space',
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published; gauge inferred from the conductor array. Cardas publishes no electrical figures for this model, so all three are derived by the kit.',
    accent: '#b07d2b',
    tags: ['clear-series', 'flagship', 'balanced'],
    notes:
      'The largest of the Cardas interconnect arrays, built to the same golden-section rule and terminated in the company own-machined connectors. Everything here is heavier: more copper, more air space in the dielectric, thicker shield, better plating. None of it changes the fact that an interconnect drives a high-impedance input through a few hundred picofarads, which is why the derived in-band response of this cable and the Iridium below it are the same to two decimal places.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Power',
    cableType: 'power',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['15a', 'golden-section-litz', 'cardas-connectors'],
    notes:
      'A mains cord built with the same golden-section conductor bundles as the signal cables, terminated in Cardas own rhodium-plated IEC and wall connectors. The connectors are the part with an unambiguous function: contact area and plating determine contact resistance and how well the joint survives repeated insertion. The engine applies no signal processing to power cords, and this entry does not pretend otherwise.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear Beyond Power',
    cableType: 'power',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 11,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published by Cardas; gauge inferred from the stated current rating and the size of the cord. No DSP is applied to power cords in this engine.',
    accent: '#b07d2b',
    tags: ['20a', 'high-current', 'flagship'],
    notes:
      'The heaviest cord Cardas builds, with large litz conductor bundles and a 20 A connector option for amplifiers wired to a dedicated circuit. Described purely as an object it is an excellent piece of cable manufacturing: thick, well terminated, properly rated. Described in terms of this engine it is a zero: mains cords sit upstream of a rectifier, a regulator and a reservoir bank, and no signal processing is applied for them.',
  }),

  makeCable({
    mfr: 'Cardas Audio',
    model: 'Clear USB',
    cableType: 'usb',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b07d2b',
    tags: ['usb-2.0', 'shielded', 'compliant'],
    notes:
      'A 90 ohm USB 2.0 assembly with a twisted data pair, separate power conductors and a foil-plus-braid shield in Cardas moulded connectors. Holding the differential impedance near 90 ohms through the connector transition is the genuinely difficult part of building a USB cable and it is a measurable, specified property. USB carries no analog waveform, so the engine applies no signal processing to it.',
  }),

  // =========================================================================
  // Nordost -- Holliston, Massachusetts
  // =========================================================================
  makeCable({
    mfr: 'Nordost',
    model: 'Blue Heaven LS Speaker Cable',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 16,
    dielectric: 'FEP with spiral mono-filament air gap',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor material, the mono-filament construction and the flat layout are as Nordost describes them. Gauge is the inferred total copper cross-section of the conductor set. Nordost does not publish R, L and C in a form recalled reliably here, so all three are derived.',
    accent: '#8fa9bd',
    tags: ['micro-mono-filament', 'flat-ribbon', 'entry-line'],
    notes:
      'Solid silver-plated OFC conductors laid side by side in a flat FEP ribbon, each one wrapped in a spiral filament that holds the extruded jacket off the wire so most of the surrounding volume is air. The flat spaced layout is a low-inductance geometry, but the total copper is modest at around 16 AWG, so loop resistance is near 0.026 ohm/m and a 2 m run adds about 0.053 ohm. Into a 4 ohm load that is roughly 0.11 dB of level loss -- the honest cost of a thin cable, and something the engine will show against a heavier one.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Heimdall 2 Speaker Cable',
    cableType: 'speaker',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 15,
    dielectric: 'FEP with spiral mono-filament air gap',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published. Gauge is inferred from conductor count and size rather than quoted, and the electrical figures are derived from it.',
    accent: '#8fa9bd',
    tags: ['micro-mono-filament', 'norse-series', 'flat-ribbon'],
    notes:
      'Norse-series construction: more silver-plated solid conductors than the Leif cables below it, same mono-filament wrap, same flat extrusion. The added conductors lower loop resistance to about 0.021 ohm/m, so a 2 m pair drops roughly 0.042 ohm rather than 0.053. That is a real if small difference into a low-impedance load, and it is the only difference between these two cables that this engine can compute.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Frey 2 Speaker Cable',
    cableType: 'speaker',
    price: usd(6200, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 14,
    dielectric: 'FEP with spiral mono-filament air gap',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published; gauge inferred from the conductor set. Electrical values derived by the kit.',
    accent: '#8fa9bd',
    tags: ['micro-mono-filament', 'norse-series', 'flat-ribbon'],
    notes:
      'A wider flat array of the same silver-plated solid conductors, with the mono-filament spiral keeping the FEP jacket off each wire so the effective dielectric is largely air. Air has the lowest dielectric constant available short of vacuum, and the direct consequence is lower capacitance per metre for the same conductor spacing -- a measurable property of the cable, not a claim about what it sounds like. Total copper is around 14 AWG.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Valhalla 2 Speaker Cable',
    cableType: 'speaker',
    price: usd(23000, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 13,
    dielectric: 'FEP with dual spiral mono-filament air gap',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published by Nordost; gauge is the inferred total cross-section of the conductor array. No published electrical figures are used, so all three are derived.',
    accent: '#8fa9bd',
    tags: ['dual-mono-filament', 'reference-series', 'flagship'],
    notes:
      'The reference-series version: a dual mono-filament wrap on extreme-purity silver-plated OFC conductors, in the widest flat array Nordost extrudes. Around 13 AWG of total copper puts loop resistance near 0.013 ohm/m, so a 2 m run drops about 0.026 ohm. Everything else that separates it from the Leif cables -- purity, filament count, tolerances, connector machining -- is manufacturing that the three electrical numbers do not record, and this database will not invent a fourth number to record it.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'White Lightning Interconnect',
    cableType: 'interconnect',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'FEP',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa9bd',
    tags: ['leif-series', 'entry-line', 'fep-dielectric'],
    notes:
      'The cheapest cable Nordost makes: solid silver-plated OFC conductors in FEP, twisted, with moulded RCA plugs. Twisted-pair geometry gives around 70 pF/m, so a 1 m pair loads a source with roughly 70 pF and corners far above the audio band from any sane output impedance. Included here as the bottom of a range whose top costs seventy times as much and computes to the same in-band answer.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Blue Heaven Interconnect',
    cableType: 'interconnect',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'FEP with spiral mono-filament air gap',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa9bd',
    tags: ['micro-mono-filament', 'leif-series', 'balanced'],
    notes:
      'Solid silver-plated OFC conductors with the mono-filament spiral and a braided shield, offered single-ended or balanced. The construction difference against the White Lightning is the air gap between conductor and jacket, which lowers the effective dielectric constant and therefore capacitance per metre. On a 1 m interconnect that is a change to a corner frequency already in the megahertz, and the engine reports the in-band consequence as it finds it.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Heimdall 2 Interconnect',
    cableType: 'interconnect',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'FEP with spiral mono-filament air gap',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa9bd',
    tags: ['micro-mono-filament', 'norse-series', 'balanced'],
    notes:
      'Norse-series interconnect: larger solid silver-plated conductors, mono-filament wrap, braided shield, and Nordost machined connectors. In balanced form the shield is carried to the connector shell rather than to pin 1 signal ground on both ends, which is a wiring choice with a checkable effect on ground-loop behaviour. Capacitance remains in the region of 70 pF/m, so the computed in-band effect is nil.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Blue Heaven Power Cord',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 14,
    currentRatingA: 15,
    dielectric: 'FEP with spiral mono-filament air gap',
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa9bd',
    tags: ['15a', 'flat-geometry', 'micro-mono-filament'],
    notes:
      'A mains cord built the way Nordost builds signal cable: solid silver-plated OFC conductors, mono-filament spiral, FEP extrusion, laid flat rather than bundled. It is rated for a 15 A circuit and it is unusually flat and stiff, which is a real handling characteristic worth knowing before buying one. Power cords receive no signal processing in this engine.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Frey 2 Power Cord',
    cableType: 'power',
    price: usd(2800, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 13,
    currentRatingA: 15,
    dielectric: 'FEP with spiral mono-filament air gap',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published; gauge inferred from conductor count. No DSP is applied to power cords, so the derived electrical figures are carried for completeness only.',
    accent: '#8fa9bd',
    tags: ['15a', 'norse-series', 'flat-geometry'],
    notes:
      'The Norse-series cord: more of the same silver-plated solid conductors in the flat mono-filament arrangement, with a C19 option for high-current inlets. As a piece of engineering it is consistent with the rest of the range and clearly made to the same standard. As a contributor to the audio in this simulation it is exactly nothing, and saying so is more useful than pretending otherwise.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Heimdall 2 USB 2.0',
    cableType: 'usb',
    price: usd(800, 'APPROXIMATE', 2024, 'Approximate price for a 2 m A-to-B assembly.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    dielectric: 'FEP',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8fa9bd',
    tags: ['usb-2.0', 'norse-series', 'shielded'],
    notes:
      'Silver-plated solid conductors in FEP with a twisted 90 ohm data pair and separate power conductors, foil and braid shielded. Nordost keeps the data pair impedance to the USB specification, which is the property that determines whether the link works at 480 Mbit/s. There is no analog waveform in a USB cable, so no signal processing is applied to this type at all.',
  }),

  makeCable({
    mfr: 'Nordost',
    model: 'Blue Heaven Ethernet',
    cableType: 'ethernet',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Gigabit Ethernet, RJ45 terminated',
    dielectric: 'FEP',
    shielding: 'Shielded pairs with an overall screen',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Nordost builds Ethernet leads on the same conductor and dielectric platform as the rest of the Leif range, and the material and geometry here follow that. The exact category rating is not asserted because it is not reliably recalled, so the entry states gigabit operation only.',
    accent: '#8fa9bd',
    tags: ['shielded', 'leif-series', 'solid-conductors'],
    notes:
      'Four solid silver-plated OFC pairs in FEP with an overall screen, terminated in RJ45. The build is the same platform as the Leif signal cables, which makes it stiffer and less flexible than an ordinary patch lead. Ethernet frames are checksummed and retransmitted on error and a streamer buffers well ahead of playback, so the engine applies no signal processing to this cable type.',
  }),

  // =========================================================================
  // Transparent Audio -- Saco, Maine
  // =========================================================================
  makeCable({
    mfr: 'Transparent Audio',
    model: 'MusicWave Plus Speaker Cable',
    cableType: 'speaker',
    price: usd(800, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor material and the flat multi-gauge layout are as Transparent describes them; the gauge is the inferred total copper cross-section. The moulded network in series with the cable is a passive filter whose contents Transparent does not publish, and the engine models nothing for it -- only the conductors.',
    accent: '#3fa7a0',
    tags: ['network-box', 'length-matched', 'entry-line'],
    notes:
      'Multiple sizes of oxygen-free copper conductor laid flat and parallel, with the moulded network that defines the brand fitted near the amplifier end. That box is a passive filter network which Transparent calibrates to the exact length of the run, which is why these cables are built to order at a stated length and priced per length rather than per metre. The engine computes only the conductors: about 0.017 ohm/m of loop resistance, so roughly 0.04 ohm over a 2.4 m run.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Super Speaker Cable',
    cableType: 'speaker',
    price: usd(1700, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described by the maker; gauge inferred from cable size. Nothing is modelled for the series network module, whose contents are not published.',
    accent: '#3fa7a0',
    tags: ['network-box', 'length-matched', 'mid-line'],
    notes:
      'A heavier flat conductor array than the MusicWave Plus with a correspondingly larger network module. Because the network is tuned per length, a Transparent cable cannot simply be cut or extended after the fact -- a practical ownership consequence that follows directly from the construction. On the conductors alone, 13 AWG gives about 0.013 ohm/m, or 0.032 ohm over the standard eight-foot run.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Ultra Speaker Cable',
    cableType: 'speaker',
    price: usd(4000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described; gauge inferred. The series network module is not modelled because its contents are not published, and guessing at them would be fabrication.',
    accent: '#3fa7a0',
    tags: ['network-box', 'length-matched', 'upper-line'],
    notes:
      'More copper again in the same flat arrangement, with a larger moulded network and higher-grade spades. Loop resistance drops to about 0.010 ohm/m, which over 2.4 m is 0.025 ohm and around 0.05 dB into a 4 ohm minimum. Everything above that in this cable lives inside a sealed box the manufacturer will not describe, so this database records that the box exists and declines to model its contents.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(10000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described; gauge inferred from cable size. Network module contents unpublished and therefore unmodelled.',
    accent: '#3fa7a0',
    tags: ['network-box', 'length-matched', 'flagship'],
    notes:
      'The reference-level flat array with the largest network module in the standard range. At around 11 AWG the conductors contribute about 0.008 ohm/m, so a 2.4 m run adds roughly 0.020 ohm -- functionally the same as any other well-made heavy cable of that gauge. The engine will therefore report this cable and a 12 AWG one as near-identical, which is the accurate result rather than an evasive one.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'MusicLink Plus Interconnect',
    cableType: 'interconnect',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Conductor material and shielding as described by Transparent; gauge inferred. The moulded network in series with the conductors is not modelled.',
    accent: '#3fa7a0',
    tags: ['network-box', 'balanced', 'entry-line'],
    notes:
      'A shielded twisted pair of oxygen-free copper with a small moulded network near the source end. As with the speaker cables the network is length-specific, which is why the price is quoted for a stated length. The conductors present around 70 pF/m, so a 1 m pair puts about 70 pF across a source output -- a corner frequency in the megahertz from any normal preamp.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Ultra Interconnect',
    cableType: 'interconnect',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described; gauge inferred. Network module unmodelled, contents unpublished.',
    accent: '#3fa7a0',
    tags: ['network-box', 'balanced', 'upper-line'],
    notes:
      'The upper-line interconnect: matched conductors, a heavier multi-layer shield and a larger network housing. The shielding is the part with an unambiguous mechanism -- more coverage means better rejection of externally coupled RF, which is measurable at the connector rather than inferred from listening. Capacitance stays near 70 pF/m and the in-band electrical effect the engine computes is nil.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Reference Interconnect',
    cableType: 'interconnect',
    price: usd(6500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described; gauge inferred from cable size. The series network is not modelled.',
    accent: '#3fa7a0',
    tags: ['network-box', 'balanced', 'flagship'],
    notes:
      'Reference-level conductors and shielding with the largest interconnect network module Transparent fits. Physically it is a substantial cable with heavy machined connectors, and the connectors are the component with a measurable specification: contact resistance and retention force. The three electrical numbers for a 1 m interconnect are dominated by capacitance in the tens of picofarads, which is why the engine reports no meaningful in-band difference between this and the MusicLink Plus.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'PowerLink Plus Power Cord',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3fa7a0',
    tags: ['15a', 'entry-line'],
    notes:
      'A 14 AWG three-conductor mains cord with moulded terminations, rated for a 15 A circuit. Gauge, rating and connector quality are the whole of the specification that matters here, and they are all real. No signal processing is applied to power cords in this engine, so this entry changes the chain description and the total price and leaves the audio identical.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Premium Power Cord',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Shielded, with a filter module at the IEC end',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as described by Transparent; gauge inferred from cord size and rating. The filter module is a real component whose contents are not published; nothing is modelled for it, and nothing is modelled for the cord either.',
    accent: '#3fa7a0',
    tags: ['15a', 'shielded', 'filter-module'],
    notes:
      'A heavier shielded cord with a filter module moulded at the IEC end, in the same house style as the signal cables. Twelve-gauge conductors and a shield connected at one end are ordinary, verifiable mains-cable engineering. Whatever the module does upstream of a rectifier and a regulator, the engine applies no signal processing to power cords, so nothing is claimed for it here.',
  }),

  makeCable({
    mfr: 'Transparent Audio',
    model: 'Premium USB Cable',
    cableType: 'usb',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Transparent lists a USB assembly in this position of the range. Conductor material, gauge and shielding here follow standard compliant USB 2.0 practice rather than a published Transparent specification, and are marked estimated for that reason.',
    accent: '#3fa7a0',
    tags: ['usb-2.0', 'shielded', 'compliant'],
    notes:
      'A shielded 90 ohm USB 2.0 assembly with separate data and power conductors. The specification a USB cable has to meet is a differential impedance and a maximum skew, both of which are testable on a bench. The engine applies no signal processing to USB, and this entry makes no claim beyond its construction.',
  }),

  // =========================================================================
  // MIT Cables -- Rocklin, California
  // =========================================================================
  makeCable({
    mfr: 'MIT Cables',
    model: 'Shotgun S1 Speaker Interface',
    cableType: 'speaker',
    price: usd(1600, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'shotgun',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'MIT publishes no conductor gauge, geometry or electrical figures for this model. The gauge and geometry recorded here are inferred from the physical construction -- a doubled cable run per channel into a moulded module -- and are marked estimated. Nothing is modelled for the module itself.',
    accent: '#c0563f',
    tags: ['network-box', 'doubled-run', 'mid-line'],
    notes:
      'Shotgun construction in the literal sense: two full cable runs per channel into one moulded module at the amplifier end. Doubling the conductors halves the loop resistance for the same conductor type, which is the one part of this design with an unambiguous electrical consequence and is what the derived figures reflect. MIT describes the module in terms of articulation poles, a phrase of the company own that names nothing this database can measure, so the module contributes nothing to the model.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Matrix HD 60 Speaker Interface',
    cableType: 'speaker',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge and geometry are inferred from the size and construction of the cable, since MIT publishes neither. The series network module is not modelled because its contents are unpublished.',
    accent: '#c0563f',
    tags: ['network-box', 'multi-conductor-bundle', 'mid-line'],
    notes:
      'A round multi-conductor bundle terminated into MIT largest mid-range network housing. Physically the cable is a set of insulated copper groups in concentric layers under one jacket, which is an ordinary low-inductance arrangement; the module is the part MIT sells the design on. Only the conductors are modelled here, at roughly 0.010 ohm/m of loop resistance for a 2.4 m run.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Matrix HD 90 Speaker Interface',
    cableType: 'speaker',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge and geometry inferred from cable size; MIT publishes no construction detail or electrical figures. Network module unmodelled.',
    accent: '#c0563f',
    tags: ['network-box', 'multi-conductor-bundle', 'upper-line'],
    notes:
      'The larger Matrix bundle with a bigger module and heavier terminations. Increasing the conductor count is the change with a computable result: around 11 AWG total, so about 0.008 ohm/m and roughly 0.020 ohm across a standard eight-foot run. What the module adds is not published, is not measurable from outside, and is therefore not modelled rather than guessed at.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Magnum MA Speaker Interface',
    cableType: 'speaker',
    price: usd(8500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Construction inferred from the physical cable; no gauge, geometry or electrical figures are published for this model. The network module is not modelled.',
    accent: '#c0563f',
    tags: ['network-box', 'upper-line', 'multi-conductor-bundle'],
    notes:
      'One of MIT largest speaker interfaces, with a substantial moulded network and a thick multi-conductor bundle. The bundle is what the engine reads: enough copper to keep loop resistance near 0.008 ohm/m, which over a normal run is a small fraction of a decibel into any load. The module is a sealed passive assembly of unpublished contents and is recorded as present, not as effective.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Oracle MA-X2 Speaker Interface',
    cableType: 'speaker',
    price: usd(25000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge and geometry inferred from the size of the cable. MIT publishes no electrical data for the Oracle series, and the adjustable network module is not modelled.',
    accent: '#c0563f',
    tags: ['network-box', 'adjustable-module', 'flagship'],
    notes:
      'The flagship Oracle interface, with a large network housing carrying user-adjustable settings and a very heavy conductor bundle. Around 10 AWG of copper puts loop resistance near 0.0066 ohm/m, so the cable itself drops about 0.016 ohm over an eight-foot run -- the point beyond which more copper changes nothing measurable. The adjustable module has no published transfer function, so the engine applies none.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Shotgun S3 Interconnect',
    cableType: 'interconnect',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'shotgun',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge and geometry inferred from the doubled-run construction; MIT publishes no figures. The network module in the housing is not modelled.',
    accent: '#c0563f',
    tags: ['network-box', 'doubled-run', 'balanced'],
    notes:
      'Two parallel shielded runs per channel into a single moulded housing. Paralleling conductors halves series resistance and roughly doubles capacitance, and on an interconnect only the second of those is worth a moment: about 160 pF for a 1 m pair, which from a 600 ohm output corners above 1.6 MHz. The housing contents are unpublished and are not modelled.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Magnum M3 Interconnect',
    cableType: 'interconnect',
    price: usd(3500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Construction inferred from the physical cable; no published gauge, geometry or electrical figures exist for this model. Network module unmodelled.',
    accent: '#c0563f',
    tags: ['network-box', 'balanced', 'upper-line'],
    notes:
      'A shielded coaxial-style interconnect terminating in a large moulded network housing, available single-ended or balanced. Coaxial geometry with a solid dielectric sits near 85 pF/m, so a 1 m run presents under 100 pF and corners far above the band from any normal source. Everything else this cable claims lives in the housing, which is sealed and undocumented, so the engine reads the conductors only.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Oracle MA-X2 Interconnect',
    cableType: 'interconnect',
    price: usd(12000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge and geometry inferred. MIT publishes no electrical data for the Oracle interconnects, and the large adjustable network housing is not modelled.',
    accent: '#c0563f',
    tags: ['network-box', 'adjustable-module', 'flagship'],
    notes:
      'The Oracle interconnect: a heavy shielded conductor set into the largest network housing MIT builds for line level, with adjustable settings on the box. The cable portion is unremarkable in the technical sense -- a well-shielded pair with capacitance in the tens of picofarads per metre. This entry exists so a chain using one can be described accurately, with the module recorded as present and explicitly unmodelled.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Z-Cord II',
    cableType: 'power',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge inferred from the stated 15 A service and the size of the cord; MIT publishes no conductor detail. Power cords receive no DSP in this engine.',
    accent: '#c0563f',
    tags: ['15a', 'filter-module', 'entry-line'],
    notes:
      'A three-conductor mains cord with an MIT filter module moulded into the run, rated for 15 A service. The gauge and the rating are ordinary and adequate, and the module is a passive assembly of unpublished contents sitting on the mains side of the component power supply. The engine applies no signal processing to power cords, so this cable contributes its description and its price only.',
  }),

  makeCable({
    mfr: 'MIT Cables',
    model: 'Shotgun AC 1',
    cableType: 'power',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 20,
    shielding: 'Shielded, with a moulded filter module',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge inferred from the current rating and cord size. MIT publishes no construction or electrical detail; the filter module is not modelled, and neither is the cord, because no DSP is applied to this cable type.',
    accent: '#c0563f',
    tags: ['20a', 'high-current', 'filter-module'],
    notes:
      'The larger MIT cord: heavier conductors, a shield, a bigger moulded filter module and a 20 A connector option for amplifiers on a dedicated line. Twelve-gauge conductors rated at 20 A is a real and useful specification for a power amplifier with a large transformer and a big reservoir bank. Everything downstream of the wall socket in this engine ignores it, and that is stated rather than hidden.',
  }),

  // =========================================================================
  // Wireworld -- Fort Lauderdale, Florida
  // =========================================================================
  makeCable({
    mfr: 'Wireworld',
    model: 'Luna 8 Speaker Cable',
    cableType: 'speaker',
    price: usd(160, 'CURRENT_RETAIL', 2024, 'Price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 16,
    dielectric: 'Composilex 3',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'entry-line', 'flat-conductors'],
    notes:
      'The entry point of the DNA Helix design: flat copper conductors laid in parallel planes with alternating polarity, insulated in Wireworld Composilex compound. Flat conductors close together is the low-inductance, high-capacitance end of the trade-off, which suits a loudspeaker load. At 16 AWG total the loop resistance is around 0.026 ohm/m, so a 2.5 m run drops about 0.066 ohm -- roughly 0.14 dB into 4 ohms, which is the real cost of choosing the cheapest cable in the range.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Oasis 8 Speaker Cable',
    cableType: 'speaker',
    price: usd(500, 'CURRENT_RETAIL', 2024, 'Price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'flat-parallel',
    gauge: 14,
    dielectric: 'Composilex 3',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'ohno-continuous-cast', 'mid-line'],
    notes:
      'The same flat DNA Helix layout in Ohno continuous-cast copper, which is drawn so the grain boundaries run along the length of the conductor rather than across it. That is a metallurgical fact about the wire; its effect on bulk resistivity at audio frequencies is within a per cent of ordinary oxygen-free copper, which is why the derived resistance here is set by the 14 AWG cross-section rather than the casting process.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Eclipse 8 Speaker Cable',
    cableType: 'speaker',
    price: usd(1400, 'CURRENT_RETAIL', 2024, 'Price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'flat-parallel',
    gauge: 12,
    dielectric: 'Composilex 3',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'ohno-continuous-cast', 'upper-line'],
    notes:
      'A wider flat conductor array in OCC copper, around 12 AWG in total. The extra copper takes loop resistance to about 0.010 ohm/m, so a 2.5 m pair drops roughly 0.026 ohm against the 0.066 ohm of the Luna -- about 0.09 dB rather than 0.14 dB into a 4 ohm minimum. That difference is small, it is real, and it is computed rather than asserted.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Platinum Eclipse 8 Speaker Cable',
    cableType: 'speaker',
    price: usd(8000, 'APPROXIMATE', 2024, 'Approximate price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'flat-parallel',
    gauge: 12,
    dielectric: 'Composilex 3',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'solid-silver', 'flagship'],
    notes:
      'The flagship DNA Helix built from solid silver conductors rather than copper. Silver resistivity is about eight per cent below copper, so for the same 12 AWG cross-section loop resistance falls from roughly 0.0104 to 0.0096 ohm/m -- a difference of 0.002 ohm over a 2.5 m run, or about 0.005 dB into 4 ohms. That is the entire electrical consequence of the metal change, and the engine will report it as such.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Terra 8 Interconnect',
    cableType: 'interconnect',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 24,
    dielectric: 'Composilex 3',
    shielding: 'Foil and drain',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'entry-line', 'flat-conductors'],
    notes:
      'Flat copper conductors in the DNA Helix arrangement with a foil shield, in moulded RCA plugs. The flat geometry that minimises inductance also raises capacitance -- around 300 pF/m for this arrangement -- so a 1 m pair presents roughly 300 pF to the source. From a 100 ohm solid-state output that corners above 5 MHz; from a 2.5 kohm tube output it lands near 210 kHz, still a decade above the band but the number to check if the source impedance is high.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Solstice 8 Interconnect',
    cableType: 'interconnect',
    price: usd(250, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 23,
    dielectric: 'Composilex 3',
    shielding: 'Foil and drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'entry-line', 'balanced'],
    notes:
      'A larger flat conductor set than the Terra in the same insulation, offered balanced as well as single-ended. Adding conductor width increases capacitance a little further, which is the direction this geometry always moves; on a 1 m interconnect into a modern low-impedance output it remains inaudible and the engine says so with a number rather than a shrug.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Equinox 8 Interconnect',
    cableType: 'interconnect',
    price: usd(800, 'CURRENT_RETAIL', 2024, 'Price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'flat-parallel',
    gauge: 22,
    dielectric: 'Composilex 3',
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'ohno-continuous-cast', 'balanced'],
    notes:
      'OCC copper flat conductors with foil and braid shielding, in Wireworld machined connectors. In balanced form the two legs are geometrically matched, which is what makes common-mode rejection work at the receiving differential input -- a real, testable property of the cable rather than a description of its sound. Capacitance remains the dominant term at around 300 pF/m.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Silver Eclipse 8 Interconnect',
    cableType: 'interconnect',
    price: usd(1600, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 22,
    dielectric: 'Composilex 3',
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['dna-helix', 'silver-clad', 'upper-line'],
    notes:
      'Silver-clad OCC copper flat conductors in the DNA Helix arrangement. Plating puts silver on the surface where high-frequency current concentrates, and at audio frequencies the current is not concentrated there at all -- the skin depth in copper at 20 kHz is about half a millimetre, far thicker than these conductors. So the DC path is the copper core and the derived resistance reflects that, which is why the kit treats silver-plated copper as copper for resistivity.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Chroma 8 USB 2.0',
    cableType: 'usb',
    price: usd(60, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    dielectric: 'Composilex 3',
    shielding: 'Foil and braid',
    termination: ['USB A to B', 'USB A to C'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['usb-2.0', 'compliant', 'entry-line'],
    notes:
      'A compliant 90 ohm USB 2.0 assembly with a twisted data pair, separate power conductors and foil-plus-braid shielding. At this price it is simply a well-made cable that meets the specification, which is the whole job. The engine applies no signal processing to USB, so nothing further is claimed.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Silver Starlight 8 USB 2.0',
    cableType: 'usb',
    price: usd(400, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    dielectric: 'Composilex 3',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['usb-2.0', 'silver-clad', 'flat-conductors'],
    notes:
      'Silver-clad OCC conductors in Wireworld flat arrangement, with the data pair held to 90 ohms and the power conductors separated from it. Maintaining differential impedance through the connector transition is the hard part of USB cable manufacture and it is measurable with a time-domain reflectometer. USB audio is packetised and error-checked, so the engine applies no signal processing to this cable type.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Starlight 8 Cat8 Ethernet',
    cableType: 'ethernet',
    price: usd(150, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 8, RJ45 terminated',
    dielectric: 'Composilex 3',
    shielding: 'Individually foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['cat8', 'shielded', 'compliant'],
    notes:
      'Four individually foiled pairs under an overall braid, terminated in shielded RJ45 and built to the Cat 8 class. Cat 8 specifies the pair-to-pair crosstalk and return loss up to 2 GHz, which is a rating a laboratory can verify and which has nothing to do with the 1.4 Mbit/s a stereo PCM stream occupies. Ethernet is checksummed and retransmitted on error, so no signal processing is applied here.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Stratus 7 Power Cord',
    cableType: 'power',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    currentRatingA: 15,
    dielectric: 'Composilex 2',
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['15a', 'flat-conductors', 'entry-line'],
    notes:
      'A mains cord using the same flat conductor idea as the signal cables, in 14 AWG with a moulded IEC and plug for 15 A service. As a cord it is properly rated and well terminated. The engine applies no signal processing to power cables, so selecting it changes the chain listing and the total price and nothing in the audio.',
  }),

  makeCable({
    mfr: 'Wireworld',
    model: 'Electra 7 Power Cord',
    cableType: 'power',
    price: usd(500, 'CURRENT_RETAIL', 2024, 'Price for a 2 m cord.'),
    priceLengthM: 2,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'flat-parallel',
    gauge: 12,
    currentRatingA: 20,
    dielectric: 'Composilex 2',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a5ccc',
    tags: ['20a', 'high-current', 'ohno-continuous-cast'],
    notes:
      'Heavier OCC copper conductors in the flat arrangement, with a C19 option, rated for 20 A service. Twelve-gauge conductors keep the voltage drop under load small, which is the only place cord gauge has a demonstrable effect at all -- and that effect is on the mains side of the rectifier. No DSP is applied for power cords in this engine.',
  }),

  // =========================================================================
  // Synergistic Research -- Santa Ana, California
  // =========================================================================
  makeCable({
    mfr: 'Synergistic Research',
    model: 'Foundation Speaker Cable',
    cableType: 'speaker',
    price: usd(1000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Synergistic Research publishes no conductor gauge, geometry or electrical figures. Material, gauge and geometry here are inferred from the physical cable and the general description of the range, and are marked estimated for that reason.',
    accent: '#d1495b',
    tags: ['entry-line', 'carbon-fibre-terminations'],
    notes:
      'The entry point of the current Synergistic range: copper conductors in a twisted arrangement with the carbon-fibre-shelled terminations the company uses throughout. At roughly 12 AWG the loop resistance is near 0.010 ohm/m, so a 2.4 m run adds about 0.025 ohm in series with the loudspeaker. That figure, and the inductance that follows from the twist, are the whole of what this engine can compute for any speaker cable.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Atmosphere X Excite Speaker Cable',
    cableType: 'speaker',
    price: usd(2500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Conductor material, gauge and geometry are inferred from the description of the Atmosphere X range rather than from published specifications, which Synergistic Research does not provide. Any treatment modules the maker fits are outside the modelled path.',
    accent: '#d1495b',
    tags: ['mid-line', 'silver-plated', 'treatment-module'],
    notes:
      'Mid-range Atmosphere X construction: silver-plated copper conductors, twisted, with the small housings Synergistic fits along the run. Plating puts silver on a surface that carries no disproportionate share of the current at audio frequencies, so the resistance the engine derives comes from the copper core and the 12 AWG cross-section. The housings are recorded as part of the description and contribute nothing to the model, because nothing about their contents is published.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Galileo SX Speaker Cable',
    cableType: 'speaker',
    price: usd(10000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'air-tube',
    gauge: 11,
    dielectric: 'Air tube',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Air-tube dielectric and mixed silver and copper conductors are as the maker describes the Galileo range; gauge is inferred from the size of the cable. No electrical figures are published, so all three are derived from the inferred gauge and geometry.',
    accent: '#d1495b',
    tags: ['air-dielectric', 'flagship', 'treatment-module'],
    notes:
      'The Galileo construction suspends the conductors inside sealed tubes so the dielectric is mostly air, with a mixture of silver and copper conductors. An air dielectric with widely spaced conductors is the low-capacitance, high-inductance corner of the trade-off: the derived figures put a 2.4 m run near 2.2 uH, whose reactance at 20 kHz is about 0.27 ohm. On a loudspeaker with a low treble impedance that is the one number here worth looking at.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Foundation Interconnect',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge, conductor material and geometry are inferred from the physical cable; Synergistic Research publishes no specifications. Electrical values are derived accordingly.',
    accent: '#d1495b',
    tags: ['entry-line', 'balanced', 'carbon-fibre-terminations'],
    notes:
      'A shielded twisted pair with carbon-shelled RCA or XLR terminations. Twisted geometry sits near 70 pF/m, so a 1 m pair loads the source with about 70 pF and the low-pass corner lands in the megahertz from any ordinary output stage. The engine computes that and reports an in-band deviation of 0.00 dB, which is the correct answer for a well-built short interconnect.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Atmosphere X Euphoria Interconnect',
    cableType: 'interconnect',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Construction inferred from the range description rather than from published figures. Treatment housings fitted to the cable are outside the modelled path.',
    accent: '#d1495b',
    tags: ['mid-line', 'balanced', 'silver-plated'],
    notes:
      'Silver-plated copper conductors in a shielded twisted pair, with the housings Synergistic fits near the connectors. In balanced form the shield is carried separately from the two signal legs, which is the construction detail that determines how well the pair rejects common-mode interference. Capacitance stays around 70 pF/m and the derived in-band effect is nil.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Galileo SX Interconnect',
    cableType: 'interconnect',
    price: usd(6000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'air-tube',
    gauge: 21,
    dielectric: 'Air tube',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Air-tube construction and mixed conductors as described by the maker; gauge inferred. No published electrical figures exist, so all three are derived.',
    accent: '#d1495b',
    tags: ['air-dielectric', 'flagship', 'balanced'],
    notes:
      'Conductors suspended in sealed air tubes, with silver and copper used together. Air is the lowest-permittivity dielectric available in a cable, and the direct consequence is low capacitance -- around 28 pF/m by the derivation, so under 30 pF for a 1 m pair. From a 2 kohm tube output that corners above 2.8 MHz. The mechanism is real and its in-band consequence is nothing, both of which this entry states.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Foundation SX AC',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Gauge inferred from cord size and the 15 A service it is sold for; no conductor specifications are published. Power cords receive no DSP in this engine.',
    accent: '#d1495b',
    tags: ['15a', 'entry-line'],
    notes:
      'A three-conductor mains cord in roughly 12 AWG with carbon-shelled connectors, sold for 15 A service. The connectors are well made and the gauge is generous for the current, which is all a power cord specification consists of. The engine applies identity to power cords: the price and the description are carried into the chain, and the audio is untouched.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Galileo SX AC',
    cableType: 'power',
    price: usd(5000, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Shielded, with the maker treatment housings on the run',
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Conductor mixture as described by the maker; gauge inferred from the cord size and current rating. The housings on the cord are unpublished passive assemblies and are not modelled -- nor is the cord, since no DSP is applied to this cable type.',
    accent: '#d1495b',
    tags: ['20a', 'high-current', 'flagship'],
    notes:
      'The flagship Synergistic mains cord: heavy mixed silver and copper conductors, a shield, treatment housings on the run and carbon-shelled connectors rated to 20 A. It is a large, carefully assembled cord and the current rating is real and relevant to a big amplifier. It is also, in this engine, a component with exactly zero DSP attached to it, and this entry would rather say that plainly than imply otherwise.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Foundation USB',
    cableType: 'usb',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Conductor detail is inferred from standard compliant USB 2.0 practice, since the maker publishes none. No DSP is applied to USB in this engine.',
    accent: '#d1495b',
    tags: ['usb-2.0', 'shielded', 'entry-line'],
    notes:
      'A shielded 90 ohm USB 2.0 assembly with separate bus power conductors and carbon-shelled connector shells. The specification that governs a USB cable is differential impedance and pair skew, both testable and neither audible in themselves. USB audio is packetised and error-checked and the engine applies no processing to it.',
  }),

  makeCable({
    mfr: 'Synergistic Research',
    model: 'Foundation Ethernet',
    cableType: 'ethernet',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Gigabit Ethernet, RJ45 terminated',
    shielding: 'Shielded pairs with an overall screen',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Conductor and shielding detail inferred from standard shielded patch-cable practice; the maker publishes no category certification that is reliably recalled here, so none is asserted.',
    accent: '#d1495b',
    tags: ['shielded', 'entry-line'],
    notes:
      'Four shielded copper pairs under an overall screen with shielded RJ45 connectors. As a patch lead it is a competent shielded assembly. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of audio ahead of playback, so there is no analog path between the two ends and the engine applies no signal processing here.',
  }),

  // =========================================================================
  // Analysis Plus -- Flushing, Michigan
  // =========================================================================
  makeCable({
    mfr: 'Analysis Plus',
    model: 'Oval 12',
    cableType: 'speaker',
    price: usd(250, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', 'entry-line'],
    notes:
      'The conductor is a hollow copper tube pressed into an oval, a shape Analysis Plus arrived at from finite-element modelling of current distribution: in a solid round conductor the current crowds toward the surface as frequency rises, and in a closely spaced pair it crowds further toward the facing edges, so effective resistance climbs with frequency. Spreading the same copper around a hollow oval perimeter keeps that rise smaller. The DC loop resistance is still just gauge and material -- about 0.010 ohm/m at 12 AWG -- and in the audio band that term dominates.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Oval 9',
    cableType: 'speaker',
    price: usd(400, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 9,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published by Analysis Plus. The nine-gauge figure is the size the model is named for; it is heavier than the usual speaker-cable range and is recorded as stated. No electrical figures are published, so all three are derived.',
    accent: '#e0a458',
    tags: ['hollow-oval', 'heavy-gauge', 'mid-line'],
    notes:
      'The same hollow oval geometry in a much larger cross-section, around nine gauge. That takes loop resistance down to roughly 0.005 ohm/m, so a 2.4 m run adds about 0.013 ohm -- an eighth of a decibel becomes a fortieth. Past this point conductor size stops being the limiting factor in a speaker cable, and the honest consequence is that heavier cables in this range measure the same as this one.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Silver Oval 2',
    cableType: 'speaker',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', 'silver-plated', 'mid-line'],
    notes:
      'Hollow oval conductors with a silver plating over the copper. The plating sits where high-frequency current concentrates, which is a coherent thing to do in a radio-frequency cable and close to irrelevant at 20 kHz, where the skin depth in copper is around half a millimetre. The kit therefore derives resistance from the copper core and the 12 AWG cross-section, giving about 0.010 ohm/m, the same as the plain Oval 12.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Big Silver Oval',
    cableType: 'speaker',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    provenanceNote:
      'Construction as published; gauge inferred from the physical size of the conductor pair rather than a quoted AWG figure.',
    accent: '#e0a458',
    tags: ['hollow-oval', 'silver-plated', 'upper-line'],
    notes:
      'A larger silver-plated hollow oval pair, around 11 gauge, in the braided outer jacket Analysis Plus uses on its upper cables. The increase in copper is what moves the numbers: about 0.008 ohm/m of loop resistance, or 0.020 ohm across a standard eight-foot run. The oval cross-section keeps the pair close together, which is why this construction sits at the low-inductance end of the geometry table.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Silver Apex',
    cableType: 'speaker',
    price: usd(6500, 'APPROXIMATE', 2024, 'Approximate price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    provenanceNote:
      'Analysis Plus describes the Apex cables as silver conductors in the hollow oval geometry; the exact metallurgy and gauge are not published in a form recalled reliably here, so both are marked estimated and the electrical figures are derived from them.',
    accent: '#e0a458',
    tags: ['hollow-oval', 'solid-silver', 'flagship'],
    notes:
      'The flagship hollow oval, built in silver rather than plated copper. Silver resistivity is about eight per cent below copper, so at the same cross-section loop resistance falls from roughly 0.0083 to 0.0076 ohm/m: across a 2.4 m run that is a difference of under 0.002 ohm, or about 0.004 dB into 4 ohms. The construction is genuinely excellent and the electrical difference against the copper version is at the edge of measurement.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Copper Oval-In',
    cableType: 'interconnect',
    price: usd(250, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', 'entry-line', 'balanced'],
    notes:
      'The hollow oval conductor scaled down to line level, inside a braided shield. Coaxial construction with a solid dielectric puts capacitance near 85 pF/m, so a 1 m pair loads a source with under 100 pF; from a 600 ohm tube output that is a corner above 3 MHz. The oval conductor argument is about how current distributes at high frequency, and at line level into a high-impedance input it changes nothing the engine can find.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Solo Crystal Oval-In',
    cableType: 'interconnect',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', 'single-crystal-copper', 'balanced'],
    notes:
      'The same hollow oval line-level geometry drawn from single-crystal copper, where the casting process leaves long grains running the length of the conductor. That is a real difference in the metal and it moves bulk resistivity by well under a per cent, which on a 1 m interconnect carrying a line-level signal is not a quantity the engine can distinguish from zero. Capacitance remains the dominant term at about 85 pF/m.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Silver Apex Interconnect',
    cableType: 'interconnect',
    price: usd(2800, 'APPROXIMATE', 2024, 'Approximate price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    provenanceNote:
      'Silver conductors in the hollow oval geometry are as the maker describes the Apex line; gauge is inferred. No electrical figures are published, so all three are derived.',
    accent: '#e0a458',
    tags: ['hollow-oval', 'solid-silver', 'flagship'],
    notes:
      'The line-level Apex: silver hollow oval conductors in a braided shield with machined connectors. On an interconnect the series resistance of the conductor is already several orders of magnitude below the input impedance it drives, so changing the metal moves nothing that the load can see. This entry records the construction accurately and reports the same derived in-band result as the copper version, because that is what the physics gives.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Power Oval 2',
    cableType: 'power',
    price: usd(350, 'CURRENT_RETAIL', 2024, 'Price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', '15a'],
    notes:
      'A mains cord built from the same hollow oval conductors as the speaker cables, in roughly 12 AWG with moulded terminations for 15 A service. The oval conductor is a real and unusual piece of manufacturing and the cord is generously rated for its job. The engine applies no signal processing to power cords, so this entry contributes its construction description and its price and nothing else.',
  }),

  makeCable({
    mfr: 'Analysis Plus',
    model: 'Power Oval 10',
    cableType: 'power',
    price: usd(700, 'CURRENT_RETAIL', 2024, 'Price for a 1.8 m cord.'),
    priceLengthM: 1.8,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e0a458',
    tags: ['hollow-oval', '20a', 'high-current'],
    notes:
      'The heavy version: ten-gauge hollow oval conductors with a 20 A connector option, for an amplifier on a dedicated circuit. Ten gauge at 20 A is a comfortable margin and keeps the cord voltage drop under load small, which is the one demonstrable function of a mains cable. That function sits entirely upstream of the rectifier, and this engine applies no DSP to power cords.',
  }),
];
